// Maintenance Mode Utility
// This utility helps manage maintenance mode for EternaVaults

export const MAINTENANCE_CONFIG = {
  // Set to true to enable maintenance mode
  enabled: false,
  
  // Maintenance page URL
  maintenanceUrl: '/maintenance',
  
  // Allowed routes during maintenance (these will still work)
  allowedRoutes: [
    '/maintenance',
    '/login', // Allow login during maintenance
    '/auth/callback' // Allow auth callbacks during maintenance
  ],
  
  // Maintenance message
  message: 'We\'re currently performing scheduled maintenance to enhance your experience and strengthen our security infrastructure.',
  
  // Estimated completion time (optional)
  estimatedCompletion: null, // e.g., '2024-01-20T15:00:00Z'
  
  // Contact information for urgent matters
  emergencyContact: 'support@eternavaults.com'
};

/**
 * Check if maintenance mode is enabled
 * @returns {boolean}
 */
export const isMaintenanceMode = () => {
  // Check environment variable first (for production)
  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
    return true;
  }
  // Fall back to config file
  return MAINTENANCE_CONFIG.enabled;
};

/**
 * Check if a route is allowed during maintenance
 * @param {string} pathname - The current pathname
 * @returns {boolean}
 */
export const isAllowedRoute = (pathname) => {
  return MAINTENANCE_CONFIG.allowedRoutes.some(route => 
    pathname === route || pathname.startsWith(route)
  );
};

/**
 * Get maintenance configuration
 * @returns {object}
 */
export const getMaintenanceConfig = () => {
  return MAINTENANCE_CONFIG;
};

/**
 * Enable maintenance mode
 * Call this function to enable maintenance mode
 * Example: enableMaintenanceMode()
 */
export const enableMaintenanceMode = () => {
  MAINTENANCE_CONFIG.enabled = true;
  console.log('🔧 Maintenance mode enabled');
};

/**
 * Disable maintenance mode
 * Call this function to disable maintenance mode
 * Example: disableMaintenanceMode()
 */
export const disableMaintenanceMode = () => {
  MAINTENANCE_CONFIG.enabled = false;
  console.log('✅ Maintenance mode disabled');
};

/**
 * Set maintenance message
 * @param {string} message - Custom maintenance message
 */
export const setMaintenanceMessage = (message) => {
  MAINTENANCE_CONFIG.message = message;
};

/**
 * Set estimated completion time
 * @param {string} time - ISO string of estimated completion time
 */
export const setEstimatedCompletion = (time) => {
  MAINTENANCE_CONFIG.estimatedCompletion = time;
};

// Usage Examples:
// 
// To enable maintenance mode:
// import { enableMaintenanceMode } from './utils/maintenance';
// enableMaintenanceMode();
//
// To disable maintenance mode:
// import { disableMaintenanceMode } from './utils/maintenance';
// disableMaintenanceMode();
//
// To set a custom message:
// import { setMaintenanceMessage } from './utils/maintenance';
// setMaintenanceMessage('We\'re upgrading our servers for better performance!');
//
// To set estimated completion:
// import { setEstimatedCompletion } from './utils/maintenance';
// setEstimatedCompletion('2024-01-20T15:00:00Z');
